import React from 'react';
import {
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSnackbar } from 'notistack';

type Bloque =
    | { tipo: 'titulo'; nivel: number; texto: string }
    | { tipo: 'parrafo'; lineas: string[] }
    | { tipo: 'lista'; ordenada: boolean; items: string[] }
    | { tipo: 'tabla'; encabezado: string[]; filas: string[][] }
    | { tipo: 'codigo'; texto: string };

const RE_TITULO = /^(#{1,6})\s+(.*)$/;
const RE_VINETA = /^\s*[-*•]\s+(.*)$/;
const RE_NUMERO = /^\s*\d+[.)]\s+(.*)$/;
const RE_SEPARADOR_TABLA = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

const celdas = (linea: string): string[] =>
    linea
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c) => c.trim());

const parsear = (texto: string): Bloque[] => {
    const lineas = texto.replace(/\r\n/g, '\n').split('\n');
    const bloques: Bloque[] = [];
    let i = 0;

    while (i < lineas.length) {
        const linea = lineas[i];

        if (!linea.trim()) {
            i++;
            continue;
        }

        if (linea.trim().startsWith('```')) {
            const contenido: string[] = [];
            i++;
            while (i < lineas.length && !lineas[i].trim().startsWith('```')) {
                contenido.push(lineas[i]);
                i++;
            }
            i++;
            bloques.push({ tipo: 'codigo', texto: contenido.join('\n') });
            continue;
        }

        const titulo = RE_TITULO.exec(linea);
        if (titulo) {
            bloques.push({ tipo: 'titulo', nivel: titulo[1].length, texto: titulo[2] });
            i++;
            continue;
        }

        if (linea.trim().startsWith('|') && i + 1 < lineas.length && RE_SEPARADOR_TABLA.test(lineas[i + 1].trim())) {
            const encabezado = celdas(linea);
            const filas: string[][] = [];
            i += 2;
            while (i < lineas.length && lineas[i].trim().startsWith('|')) {
                filas.push(celdas(lineas[i]));
                i++;
            }
            bloques.push({ tipo: 'tabla', encabezado, filas });
            continue;
        }

        if (RE_VINETA.test(linea) || RE_NUMERO.test(linea)) {
            const ordenada = !RE_VINETA.test(linea);
            const re = ordenada ? RE_NUMERO : RE_VINETA;
            const items: string[] = [];
            while (i < lineas.length && re.test(lineas[i])) {
                items.push((re.exec(lineas[i]) as RegExpExecArray)[1]);
                i++;
            }
            bloques.push({ tipo: 'lista', ordenada, items });
            continue;
        }

        const parrafo: string[] = [];
        while (
            i < lineas.length &&
            lineas[i].trim() &&
            !RE_TITULO.test(lineas[i]) &&
            !RE_VINETA.test(lineas[i]) &&
            !RE_NUMERO.test(lineas[i]) &&
            !lineas[i].trim().startsWith('```') &&
            !lineas[i].trim().startsWith('|')
        ) {
            parrafo.push(lineas[i]);
            i++;
        }
        if (parrafo.length === 0) {
            parrafo.push(linea);
            i++;
        }
        bloques.push({ tipo: 'parrafo', lineas: parrafo });
    }

    return bloques;
};

const renderInline = (texto: string): React.ReactNode[] => {
    const partes = texto.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g).filter(Boolean);
    return partes.map((p, idx) => {
        if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
            return <strong key={idx}>{p.slice(2, -2)}</strong>;
        }
        if (p.startsWith('`') && p.endsWith('`') && p.length > 2) {
            return (
                <Box
                    key={idx}
                    component="code"
                    sx={{ bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: '0.9em' }}
                >
                    {p.slice(1, -1)}
                </Box>
            );
        }
        if (p.startsWith('*') && p.endsWith('*') && p.length > 2) {
            return <em key={idx}>{p.slice(1, -1)}</em>;
        }
        return <React.Fragment key={idx}>{p}</React.Fragment>;
    });
};

const TAMANIO_TITULO: Record<number, string> = { 1: '1.25rem', 2: '1.15rem', 3: '1.05rem' };

const textoPlano = (celda: string): string =>
    celda
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1');

const aTsv = (encabezado: string[], filas: string[][]): string =>
    [encabezado, ...filas].map((f) => f.map((c) => textoPlano(c).replace(/[\t\n]/g, ' ')).join('\t')).join('\n');

const aCsv = (encabezado: string[], filas: string[][]): string => {
    const escapar = (c: string) => {
        const v = textoPlano(c);
        return /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    return [encabezado, ...filas].map((f) => f.map(escapar).join(';')).join('\r\n');
};

const descargarCsv = (contenido: string) => {
    const blob = new Blob(['\uFEFF' + contenido], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const hoy = new Date();
    const sello = `${hoy.getFullYear()}${String(hoy.getMonth() + 1).padStart(2, '0')}${String(hoy.getDate()).padStart(2, '0')}-${String(hoy.getHours()).padStart(2, '0')}${String(hoy.getMinutes()).padStart(2, '0')}`;
    a.href = url;
    a.download = `consulta-ia-${sello}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

interface ChatMarkdownProps {
    texto: string;
    /** Muestra las acciones de exportación de tablas. */
    mostrarAcciones?: boolean;
}

const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ texto, mostrarAcciones = true }) => {
    const { enqueueSnackbar } = useSnackbar();
    const bloques = parsear(texto);

    const copiarTabla = async (encabezado: string[], filas: string[][]) => {
        try {
            await navigator.clipboard.writeText(aTsv(encabezado, filas));
            enqueueSnackbar('Tabla copiada. Podés pegarla en Excel.', { variant: 'success' });
        } catch {
            enqueueSnackbar('No se pudo copiar la tabla', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
            {bloques.map((b, idx) => {
                switch (b.tipo) {
                    case 'titulo':
                        return (
                            <Typography
                                key={idx}
                                sx={{ fontWeight: 600, fontSize: TAMANIO_TITULO[b.nivel] ?? '1rem', mt: 2, mb: 1 }}
                            >
                                {renderInline(b.texto)}
                            </Typography>
                        );
                    case 'lista':
                        return (
                            <Box
                                key={idx}
                                component={b.ordenada ? 'ol' : 'ul'}
                                sx={{ pl: 3, my: 1, '& li': { mb: 0.5, lineHeight: 1.6 } }}
                            >
                                {b.items.map((it, j) => (
                                    <li key={j}>{renderInline(it)}</li>
                                ))}
                            </Box>
                        );
                    case 'tabla':
                        return (
                            <Box key={idx} sx={{ my: 1.5 }}>
                                {mostrarAcciones && (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 0.5 }}>
                                        <Button
                                            size="small"
                                            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => void copiarTabla(b.encabezado, b.filas)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Copiar para Excel
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                                            onClick={() => descargarCsv(aCsv(b.encabezado, b.filas))}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Descargar CSV
                                        </Button>
                                    </Stack>
                                )}
                                <TableContainer
                                    sx={{ border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#ffffff' }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                {b.encabezado.map((h, j) => (
                                                    <TableCell key={j} sx={{ fontWeight: 600 }}>
                                                        {renderInline(h)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {b.filas.map((f, j) => (
                                                <TableRow key={j}>
                                                    {f.map((c, k) => (
                                                        <TableCell key={k}>{renderInline(c)}</TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        );
                    case 'codigo':
                        return (
                            <Box
                                key={idx}
                                component="pre"
                                sx={{
                                    bgcolor: '#263238',
                                    color: '#eceff1',
                                    p: 1.5,
                                    borderRadius: 2,
                                    overflowX: 'auto',
                                    fontSize: '0.85rem',
                                    my: 1.5,
                                }}
                            >
                                {b.texto}
                            </Box>
                        );
                    default:
                        return (
                            <Typography key={idx} component="p" sx={{ my: 1, lineHeight: 1.7 }}>
                                {b.lineas.map((l, j) => (
                                    <React.Fragment key={j}>
                                        {j > 0 && <br />}
                                        {renderInline(l)}
                                    </React.Fragment>
                                ))}
                            </Typography>
                        );
                }
            })}
        </Box>
    );
};

export default ChatMarkdown;
